import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { rowActionIconClass } from "./DataTable";

export interface RowActionMenuItem {
  label: string;
  onClick: () => void;
  /** Optional leading icon. */
  icon?: React.ReactNode;
  /** Renders the item in the destructive (red) style. */
  danger?: boolean;
}

interface RowActionMenuProps {
  items: RowActionMenuItem[];
  /** Accessible label for the trigger button. */
  ariaLabel?: string;
}

/**
 * A three-dot row action menu whose popover is rendered in a portal with fixed
 * positioning. Because it escapes the table's `overflow-x-auto` container, it
 * never triggers a horizontal scrollbar or gets clipped the way an
 * `absolute`-positioned menu inside the row does.
 */
const RowActionMenu: React.FC<RowActionMenuProps> = ({ items, ariaLabel = "Row actions" }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Anchor the menu's right edge to the trigger's right edge, just below it.
    setCoords({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    const onClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    // `true` so we catch scrolls on the table's own overflow container too.
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        // A row whose permissions leave it with no actions must not open an
        // empty panel; disable the trigger instead.
        disabled={items.length === 0}
        className={rowActionIconClass}
      >
        <EllipsisVerticalIcon className="w-5 h-5" />
      </button>

      {open && items.length > 0 &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: "fixed", top: coords.top, right: coords.right }}
            // Sized to the widest label rather than a fixed width, so longer
            // items (e.g. "Remove as Default") stay on one line.
            className="z-50 py-1 overflow-hidden bg-white border shadow-lg min-w-[11rem] w-max rounded-xl border-neutral-200 ring-1 ring-black/5"
          >
            {items.map((item, i) => (
              <button
                key={i}
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
                className={
                  "flex items-center w-full gap-2 px-4 py-2 text-sm text-left transition-colors whitespace-nowrap " +
                  (item.danger
                    ? "text-danger-600 hover:bg-danger-50"
                    : "text-neutral-700 hover:bg-primary-50 hover:text-primary-700")
                }
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default RowActionMenu;
